import styled from "styled-components";
import useModal from "../hooks/useModal";
import { ChooseProductState, ChosenProducts, ModalState } from "../store/chart";
import { FontWeight, UIColor } from "../types";
import { Button, PrimaryButton } from "./Button";
import { Text } from "./Text";

type ChooseModalProps = {
  className?: string;
  modal: ModalState;
  chooseProducts: ChooseProductState[];
  chosenProducts: ChosenProducts;
  showCategories?: boolean;
  onProductSelect: (productId: string, column: number) => void;
};

export function ChooseModal({
  className,
  modal,
  chooseProducts,
  chosenProducts,
  showCategories = true,
  onProductSelect,
}: ChooseModalProps) {
  const { open, openModal, closeModal } = useModal();
  const {
    backgroundColor,
    openButton,
    closeButton,
    footerBackgroundColor,
    additionalFilterButton,
    showAdditionalFilterButton,
  } = modal;

  return (
    <div className={className}>
      <OpenButton
        font={openButton.label.font}
        textColor={openButton.label.color}
        backgroundColor={openButton.backgroundColor}
        borderColor={openButton.borderColor}
        onClick={() => openModal()}
      >
        {openButton.label.value}
      </OpenButton>
      {open && (
        <Overlay>
          <Wrapper>
            <Modal backgroundColor={backgroundColor?.hex}>
              <Columns>
                <Column>
                  {chooseProducts.map((chooseProduct) => (
                    <ChooseGroup
                      key={chooseProduct.filter.productKindId}
                      chooseProduct={chooseProduct}
                      selectedProduct={chosenProducts[0]}
                      disabledProduct={chosenProducts[1]}
                      showCategory={showCategories}
                      onSelect={(productId) => onProductSelect(productId, 0)}
                      modal={modal}
                    />
                  ))}
                </Column>
                <Column>
                  {chooseProducts.map((chooseProduct) => (
                    <ChooseGroup
                      key={chooseProduct.filter.productKindId}
                      chooseProduct={chooseProduct}
                      selectedProduct={chosenProducts[1]}
                      disabledProduct={chosenProducts[0]}
                      showCategory={showCategories}
                      onSelect={(productId) => onProductSelect(productId, 1)}
                      modal={modal}
                    />
                  ))}
                </Column>
              </Columns>
              <Footer backgroundColor={footerBackgroundColor?.hex}>
                {showAdditionalFilterButton && additionalFilterButton && (
                  <CloseButton
                    as="a"
                    target="_blank"
                    font={additionalFilterButton.label.font}
                    href={additionalFilterButton.link}
                    backgroundColor={additionalFilterButton.backgroundColor}
                    borderColor={additionalFilterButton.borderColor}
                    textColor={additionalFilterButton.label.color}
                    additionalFilterButton
                  >
                    {additionalFilterButton.label.value}
                  </CloseButton>
                )}
                <CloseButton
                  font={closeButton.label.font}
                  textColor={closeButton.label.color}
                  backgroundColor={closeButton.backgroundColor}
                  borderColor={closeButton.borderColor}
                  onClick={() => closeModal()}
                >
                  {closeButton.label.value}
                </CloseButton>
              </Footer>
            </Modal>
          </Wrapper>
        </Overlay>
      )}
    </div>
  );
}

type ChooseGroupProps = {
  chooseProduct: ChooseProductState;
  disabledProduct: string | null;
  selectedProduct: string | null;
  showCategory?: boolean;
  modal: ModalState;
  onSelect: (productId: string) => void;
};

function ChooseGroup({
  chooseProduct,
  selectedProduct,
  disabledProduct,
  showCategory = true,
  modal,
  onSelect,
}: ChooseGroupProps) {
  return (
    <GroupContainer>
      {showCategory && (
        <GroupTitle>{chooseProduct.filter.categoryTitle}</GroupTitle>
      )}
      <div>
        {chooseProduct.products.map((product) => (
          <ProductButton
            key={product.id}
            disabled={product.id === disabledProduct}
            aria-pressed={!!selectedProduct && product.id === selectedProduct}
            onClick={() => onSelect(product.id)}
            textColor={modal.textColor}
            selected={{
              textColor: modal.selectedTextColor,
              backgroundColor: modal.selectedBackgroundColor,
            }}
          >
            {product.name}
          </ProductButton>
        ))}
      </div>
    </GroupContainer>
  );
}

const OpenButton = styled(PrimaryButton)`
  display: block;
  width: 100%;
`;

const CloseButton = styled(PrimaryButton)<{ additionalFilterButton?: boolean }>`
  min-width: ${({ theme }) => theme.getValue(124)}px;
  text-decoration: none;
  padding: ${({ theme }) => theme.getValue(12)}px;
  ${({ theme, additionalFilterButton }) =>
    additionalFilterButton &&
    `
    border-radius: ${theme.getValue(8)}px;
    margin-bottom: ${theme.getValue(10)}px; 
  `}
`;

const Overlay = styled.div`
  position: fixed;
  z-index: 30;
  overflow: auto;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
`;

const Modal = styled.div<{ backgroundColor?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${({ theme }) => theme.getValue(16)}px;
  border-radius: ${({ theme }) => theme.getValue(10)}px;
  background: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.colors.white};
`;

const Footer = styled.div<{ backgroundColor?: string }>`
  width: 100%;
  padding: ${({ theme }) => theme.getValue(12)}px;
  background: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.colors.grey9};
  display: flex;
  border-radius: ${({ theme }) => theme.getValue(10)}px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  flex-direction: column;
`;

const Columns = styled.div`
  display: flex;
  padding: ${({ theme }) =>
    `${theme.getValue(12)}px ${theme.getValue(2)}px 0px`};
`;

const Column = styled.div`
  margin: 0 ${({ theme }) => theme.getValue(10)}px;
`;

const GroupContainer = styled.div`
  & + & {
    border-top: 2px solid ${({ theme }) => theme.colors.grey8};
  }
`;

const GroupTitle = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(18)}px;
    line-height: 1;
    margin: 0;
    padding: ${({ theme }) =>
      `${theme.getValue(12)}px ${theme.getValue(10)}px`};
  }
`;

const ProductButton = styled(Button)<{
  textColor?: UIColor;
  selected?: { textColor?: UIColor; backgroundColor?: UIColor };
}>`
  font-weight: ${({ font: { weight = FontWeight.Normal } = {} }) => weight};
  font-size: ${({ theme }) => theme.getFontSize(16)}px;
  line-height: 1;
  display: block;
  width: 100%;
  padding: ${({ theme }) => `${theme.getValue(12)}px ${theme.getValue(10)}px`};
  text-align: left;
  color: ${({ theme, textColor }) => textColor?.hex || theme.colors.black};
  border-width: 1px 0 0;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.grey8};
  border-radius: 0;
  background: none;
  transition: all 0.2s;

  &[aria-pressed="true"] {
    background: ${({ theme, selected }) =>
      selected?.backgroundColor?.hex || theme.colors.green6};
    color: ${({ theme, selected }) =>
      selected?.textColor?.hex || theme.colors.white};
  }

  &:disabled {
    opacity: 0.4;
  }
`;
